import { Component, forwardRef } from '@angular/core';
import { combineLatest, mergeMap, Observable, of, switchMap, take } from 'rxjs';
import { UitnodigingenService } from '../../../uitnodiging/service/uitnodinginen.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UitnodigingId, validatedProfielId, validatedUitnodidingId } from 'parkour-web-app-dto';
import { Uitnodiging } from '../../../uitnodiging/model/uitnodiging';
import { ParkourPopupService } from '@parkour/ui';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { ParkourBackButtonDirective } from '../../../shared/directives/parkour-back-button.directive';
import { AsyncPipe } from '@angular/common';
import { UitnodigingVerzondenComponent } from '../../component/uitnodiging-verzonden/uitnodiging-verzonden.component';
import { UitnodigingBevestigdComponent } from '../../component/uitnodiging-bevestigd/uitnodiging-bevestigd.component';
import { PageLayoutComponent } from '../../../shared/components/page-layout/page-layout.component';
import { UitnodigingAanvaardComponent } from '../../component/uitnodiging-aanvaard/uitnodiging-aanvaard.component';
import { ErrorComponent } from '../../../shared/components/error-component/error.component';
import { TeamService } from '../../service/team.service';
import { TeamlidProfiel } from '../../model/teamlid.model';
import { ParkourDataPage } from '../../../shared/parkour-data-page';
import { PageContentDirective } from '../../../shared/directives/page-content.directive';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { ParkourRefreshDirective } from '../../../shared/directives/parkour-refresh-directive';

type PageData = {
  uitnodiging: Uitnodiging;
  teamlid: TeamlidProfiel | undefined;
};

@Component({
  standalone: true,
  templateUrl: './uitnodiging-details.page.html',
  imports: [
    ParkourBackButtonDirective,
    TranslateModule,
    AsyncPipe,
    UitnodigingVerzondenComponent,
    UitnodigingBevestigdComponent,
    PageLayoutComponent,
    UitnodigingAanvaardComponent,
    ErrorComponent,
    forwardRef(() => PageContentDirective),
    IonRefresher,
    IonRefresherContent,
    IonBackButton,
    IonToolbar,
    IonHeader,
    IonButtons,
    IonContent,
    IonTitle,
    ParkourRefreshDirective,
  ],
})
export class UitnodigingDetailsPage extends ParkourDataPage<PageData> {
  constructor(
    private readonly uitnodigingService: UitnodigingenService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly parkourPopupService: ParkourPopupService,
    private readonly translate: TranslatePipe,
    private readonly teamService: TeamService,
  ) {
    super();
  }

  override retrieveMainData(): Observable<PageData> {
    const uitnodiging$ = this.route.paramMap.pipe(
      take(1),
      switchMap((paramMap) =>
        this.uitnodigingService.getUitnodiging(
          validatedUitnodidingId(paramMap.get('uitnodigingId')),
        ),
      ),
    );

    const profiel$ = uitnodiging$.pipe(
      mergeMap((uitnodiging) =>
        uitnodiging.profielId
          ? this.teamService.getTeamlidInCurrentContext(validatedProfielId(uitnodiging.profielId))
          : of(undefined),
      ),
    );

    return combineLatest({
      uitnodiging: uitnodiging$,
      teamlid: profiel$,
    });
  }

  onVerwijderUitnodiging(uitnodigingId: UitnodigingId) {
    this.parkourPopupService
      .showPopup({
        title: this.translate.transform('team.uitnodiging.stopzetten-button-confirmation-title'),
        description: this.translate.transform(
          'team.uitnodiging.stopzetten-button-confirmation-description',
        ),
        icon: 'trash',
        jaText: 'Ja',
      })
      .then((result) => {
        if (result === 'yes') {
          this.verwijderUitnodiging(uitnodigingId);
        }
      });
  }

  verwijderUitnodiging(uitnodigingId: UitnodigingId) {
    this.uitnodigingService.verwijderUitnodiging(uitnodigingId).subscribe(() => {
      this.router.navigate(['../../'], { relativeTo: this.route });
    });
  }

  onBevestigUitnodiging(uitnodigingId: UitnodigingId) {
    this.uitnodigingService.bevestigUitnodiging(uitnodigingId).subscribe(() => {
      this.router.navigate(['../../'], { relativeTo: this.route });
    });
  }
}
